import type { Data } from '@strapi/types';
import type { DateTimeValue } from '@strapi/types/dist/schema/attribute';
import { useTranslations } from '@power-core/toolkit';

import { useAppContext } from '@/modules/app-context/use-app-context';
import { useDocumentsListInit } from '@/modules/document/hooks/use-documents-list-init';
import { useFilteredDocumentsListInit } from '@/modules/document/hooks/use-filtered-document-list-init';
import type { SearchableDropdownItemProps } from '@/ui-components/dropdown/types';
import type { HistoryItem } from '@/ui-components/history/types';

import type { UseProjectHistoryProps } from './types';

const FIRST_VERSION_REGEX = /_v1(?!\d)/;

const isFirstVersion = (version?: string | null): boolean => {
	return FIRST_VERSION_REGEX.test(version ?? '');
};

const getDateTimeValue = (value?: DateTimeValue | null) => {
	return value ? (value instanceof Date ? value.toISOString() : new Date(value).toISOString()) : undefined;
};

const getTime = (file?: Data.Entity<'api::document-file.document-file'> | null) => {
	if (!file) return null;

	return file.lastUpdatedAt ? getDateTimeValue(file.lastUpdatedAt) : getDateTimeValue(file.updatedAt);
};

export const useProjectHistoryParams = ({ projectDocumentId, selectedToolName, date }: UseProjectHistoryProps) => {
	const t = useTranslations();
	const { project, toolsForSelect } = useAppContext();

	const { projectDocuments: allProjectDocuments } = useDocumentsListInit({
		projectId: project?.documentId,
		isProjectOpen: !!project?.documentId,
	});

	const {
		projectDocuments: filteredProjectDocuments,
		isLoading: isProjectHistoryLoading,
		error: projectHistoryError,
	} = useFilteredDocumentsListInit({ projectId: project?.documentId, date, documentType: selectedToolName, projectDocumentId });

	const allFilters = allProjectDocuments?.reduce((acc, cur) => {
		if (cur.name) {
			acc.push({
				value: cur.documentId,
				label: cur.name,
			});
		}

		return acc;
	}, [] as SearchableDropdownItemProps[]);

	const historyItems = filteredProjectDocuments
		?.reduce<HistoryItem[]>((acc, curr) => {
			const historyItem = curr.documentFiles.map((file) => ({
				time: getTime(file) ?? '',
				title: curr.name ?? '',
				isUpdated: !isFirstVersion(file?.version),
				id: file?.documentId ?? '',
				documentFile: file,
				documentType: curr.documentType?.title ?? '',
				documentId: curr.documentId,
				region: curr.region,
			}));
			return acc.concat(historyItem);
		}, [])
		?.sort((current, next) => {
			const currentDate = new Date(current.time).getTime();
			const nextDate = new Date(next.time).getTime();
			return nextDate - currentDate;
		});

	return {
		t,
		historyItems,
		project,
		error: projectHistoryError,
		isLoading: isProjectHistoryLoading,
		allFilters,
		toolsForSelect,
	};
};
